import { Button, Form, Input, notification, Spin } from 'antd';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { checkEmployeesRvdType, dataForRvdUsersTable } from 'shared/helpers/utils';
import Breadcrumbs from 'shared/components/breadcrumbs/Breadcrumbs';
import CommonTable from 'features/tables/commonTable';
import { TCommonColumns } from 'shared/types/overtime.types';
import { TRvdGetResponse, TRvdResponseData, TUserRvdInfo, TUserRvdInfoPaginated } from 'shared/types/rvd.types';
import { DeleteOutlined, DownloadOutlined, EditOutlined, SaveOutlined, StopOutlined } from '@ant-design/icons';
import ModalCancelSubmit from 'shared/components/modalCancelSubmit/ModalCancelSubmit';
import { useNavigate, useParams } from 'react-router-dom';
import { RvdStatuses, RvdStatusesBack } from 'shared/enums/rvd';
import {
  useCheckRvdUserMutation,
  useCreateRvdUserMutation,
  useDeleteRvdMutation,
  useDeleteRvdUserMutation,
  useLazyGetRvdByIdQuery,
  useLazyGetRvdUsersQuery,
  useUpdateRvdMutation,
  useUpdateRvdUserMutation,
} from 'app/services/edpApiV1/rvd';
import { PICKER_DATE_MASK } from 'shared/constants';
import moment from 'moment';
import { checkRvdCompensationType, checkRvdCompensationTypeToBack } from 'shared/helpers/type-check';
import EmployeeInput from 'features/absence/madalAddOneAbsence/employeeInput';
import { useLazyGetDivisionEmployeesQuery } from 'app/services/edpApiV1/viewer';
import { TDivisionEmployees } from 'shared/types/types';
import { useLazyGetEmployeesQuery } from 'app/services/edpApiV1/employees';
import { downloadExcell } from 'shared/helpers/excell/excell';
import Search from 'antd/es/transfer/search';
import { useDebounce } from 'shared/hooks';
import { useAppDispatch, useAppSelector } from 'app/store';
import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';
import { viewerModel } from 'entities/viewer';
import { returnErrorOrMessage } from 'shared/helpers/errors';
import { uiModel } from 'entities/ui';
import RvdSidebar from '../createRvdRequest/sidebar/RvdSidebar';
import { RvdTabs } from '..';
import BadgeData from '../rvdArchive/bageData/BadgeData';
import style from './index.module.scss';

const FORM_ITEM_NAME = 'signatoryUpdate';
const FORM_USER_COMMENT = 'comment';

const UpdateRvd = () => {
  const [form] = Form.useForm();
  const { id } = useParams();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentSize, setCurrentSize] = useState<number>(10);
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
  const [isSidebarCreateOpen, setIsSidebarCreateOpen] = useState<boolean>(false);
  const [openCancelModal, setOpenCanelModal] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<TUserRvdInfo | undefined>();
  const [currentRvd, setCurrentRvd] = useState<TRvdGetResponse>();
  const [currentRvdUsers, setCurrentRvdUsers] = useState<TUserRvdInfoPaginated>();
  const [getEmployees, { data: employees }] = useLazyGetEmployeesQuery();
  const [search, setSearch] = useState<string | undefined>();
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);
  const currentOrganizationid = useAppSelector(viewerModel.me.selectors.getMeOrganizationId);
  const [isUpdateLoading, setIsUpdateLoading] = useState<boolean>(false);
  const profile = useAppSelector(viewerModel.me.selectors.selectViewerProfile);

  const debouncedSearch = useDebounce(search);
  const [isLoadAllowed, setIsLoadAllowed] = useState<boolean>(false);

  const [signatory, setSignatory] = useState<TDivisionEmployees | null>(null);
  const [getSignatories, { data: signatories }] = useLazyGetDivisionEmployeesQuery();

  const [getRvdById, { data: rvdResponse, isLoading: isRvdLoading }] = useLazyGetRvdByIdQuery();

  const navigate = useNavigate();

  useEffect(() => {
    if (currentRvd?.employees) {
      let isUserWrong = false;
      for (const user of currentRvd?.employees) {
        if (user.comment) {
          setIsLoadAllowed(false);
          isUserWrong = true;
        }
      }
      if (!isUserWrong) {
        setIsLoadAllowed(true);
      }
    }
  }, [currentRvd?.employees]);

  const usersToMutate = useRef<{ update: TUserRvdInfo[]; delete: number[]; create: TUserRvdInfo[] }>({
    update: [],
    delete: [],
    create: [],
  });

  const [deleteUserQuery] = useDeleteRvdUserMutation();
  const [updateUserQuery] = useUpdateRvdUserMutation();
  const [createUserQuery] = useCreateRvdUserMutation();
  const [deleteRvdQuery, { isLoading: isDeleteLoading }] = useDeleteRvdMutation();
  const [updateRvdQuery] = useUpdateRvdMutation();
  const [checkErrors, { isLoading: isCheckLoading }] = useCheckRvdUserMutation();

  const dispatch = useAppDispatch();

  const breadCrumbs = [
    { name: 'Работа в выходной день', link: '/rvd' },
    { name: 'Редактировать РВД', link: `/rvd/update/${id}` },
  ];

  const [getRvds, { data: rvdUserResponse, isFetching: isUserFetching, isLoading: isUserLoading }] =
    useLazyGetRvdUsersQuery();

  useEffect(() => {
    getRvdById({ id: id ? +id : 0 });
    getRvds({
      size: currentSize,
      page: currentPage,
      wowId: id ? +id : 0,
    });
  }, []);

  useEffect(() => {
    getRvds({
      size: currentSize,
      page: currentPage,
      wowId: id ? +id : 0,
      personnelNumber: debouncedSearch,
    });
  }, [debouncedSearch]);

  const onClear = () => {
    getRvds({
      size: currentSize,
      page: currentPage,
      wowId: id ? +id : 0,
    });
  };

  useEffect(() => {
    if (employees?.data) {
      const [user] = employees.data;
      form.setFieldValue(FORM_ITEM_NAME, user.id);
      setSignatory({ ...user, division: user.division?.name || '', position: '' });
      getSignatories({
        page: 1,
        size: 10,
        search: `${user.lastName} ${user.name} ${user.middleName}`,
        divisionId: user.division?.divisionId,
        allAvailableOrganizations,
      });
    }
  }, [employees]);

  useEffect(() => {
    if (rvdResponse) {
      form.setFieldValue(FORM_USER_COMMENT, rvdResponse.userComment);

      getEmployees({
        isActive: true,
        page: 1,
        size: 1,
        personnelNumber: rvdResponse.docSignatory,
        allAvailableOrganizations,
        organizationId: currentOrganizationid,
      });
    }
  }, [rvdResponse]);

  useEffect(() => {
    if (rvdResponse) {
      setCurrentRvd(rvdResponse);
    }
  }, [rvdResponse]);

  useEffect(() => {
    if (rvdUserResponse) {
      setCurrentRvdUsers(rvdUserResponse);
    }
  }, [rvdUserResponse]);

  const download = () => {
    downloadExcell(currentRvd);
  };

  const onEmployeeDelete = (id: number) => {
    const justCreated = usersToMutate.current.create.find(elem => elem.id === id);
    if (justCreated) {
      usersToMutate.current.create = usersToMutate.current.create.filter(elem => elem.id !== id);
    } else {
      usersToMutate.current.delete.push(id);
    }
    const newCurrentUsers: TUserRvdInfo[] = [...currentRvdUsers!.data].filter(user => user.id !== id);
    setCurrentRvdUsers({ ...currentRvdUsers!, data: newCurrentUsers });
  };

  const rowClicked = (id: number) => {
    if (rvdResponse?.status === RvdStatusesBack.APPROVED || rvdResponse?.status === RvdStatusesBack.CANCELED) {
      return;
    }
    if (currentRvd?.hr?.id !== profile?.id) {
      return;
    }
    const user = currentRvdUsers?.data.find(item => item.id === id);

    if (user) {
      const fixedUser = {
        ...user,
        toDate: moment(user.toDate).format(PICKER_DATE_MASK),
        type: checkRvdCompensationType(user.type),
      };
      setCurrentUser(fixedUser);
    }
    setIsSidebarOpen(prev => !prev);
  };

  const onClose = () => {
    setIsSidebarOpen(false);
  };

  const onCreateClose = () => {
    setIsSidebarCreateOpen(false);
  };

  const onFinish = (rvdUser: TUserRvdInfo) => {
    const justCreated = usersToMutate.current.create.find(elem => elem.id === rvdUser.id);
    if (justCreated) {
      usersToMutate.current.create = usersToMutate.current.create.map(elem => {
        if (elem.id === rvdUser.id) {
          return rvdUser;
        }
        return elem;
      });
    } else {
      usersToMutate.current.update.push(rvdUser);
    }
    const newRvdUsers = currentRvdUsers?.data.map(user => {
      if (user.id === rvdUser.id) {
        return rvdUser;
      }
      return user;
    });
    setIsSidebarOpen(false);
    setCurrentRvd({ ...currentRvd! });
    setCurrentRvdUsers({ ...currentRvdUsers!, data: newRvdUsers ? newRvdUsers : [] });
  };

  const onCreateFinish = (rvdUser: TUserRvdInfo) => {
    const rvd = { ...currentRvd! };
    const rvdUserId = Math.random();
    rvdUser.id = rvdUserId;
    const rvdUsers = [...(currentRvdUsers?.data ? currentRvdUsers?.data : []), rvdUser];
    usersToMutate.current.create.push(rvdUser);
    setCurrentRvd(rvd);
    setCurrentRvdUsers({ ...currentRvdUsers!, data: rvdUsers ? rvdUsers : [] });
    setIsSidebarCreateOpen(false);
  };

  const columns: TCommonColumns = [
    {
      title: 'Дата работ',
      dataIndex: 'toDate',
      key: 'toDate',
    },
    {
      title: 'ФИО',
      dataIndex: 'fio',
      key: 'fio',
    },
    {
      title: 'Табельный номер',
      dataIndex: 'personnelNumber',
      key: 'personnelNumber',
    },
    {
      title: 'Подразделение',
      dataIndex: 'division',
      key: 'division',
    },
    {
      title: 'Время работы',
      dataIndex: 'duration',
      key: 'duration',
    },
    {
      title: 'Способ компенсации',
      dataIndex: 'type',
      key: 'type',
    },
  ];

  if (
    !(rvdResponse?.status === RvdStatusesBack.APPROVED || rvdResponse?.status === RvdStatusesBack.CANCELED) &&
    currentRvd?.hr?.id === profile?.id
  ) {
    columns.push({
      title: 'Действие',
      dataIndex: 'actions',
      key: 'actions',
      render: (_, record) => (
        <span
          className={`${style['table-delete']} ${
            currentRvd?.status === RvdStatuses.LOADED_CPRODUCT ? style.disabled : ''
          }`}
          onClick={e => {
            e.stopPropagation();
            if (currentRvd?.status !== RvdStatuses.LOADED_CPRODUCT) {
              onEmployeeDelete(record.id!);
            }
          }}
        >
          Удалить
        </span>
      ),
    });
  }

  const onSubmitCancelModal = () => {
    setOpenCanelModal(false);
    dispatch(uiModel.Tabs.actions.setActive(RvdTabs.my));
    navigate('/rvd');
  };

  const onCancelCancelModal = () => {
    setOpenCanelModal(false);
  };

  const cancelModalOpen = () => {
    if (currentRvd?.status !== RvdStatuses.LOADED_CPRODUCT) {
      setOpenCanelModal(true);
    } else {
      dispatch(uiModel.Tabs.actions.setActive(RvdTabs.my));

      navigate('/rvd');
    }
  };

  const deleteRvd = async () => {
    await deleteRvdQuery({ id: id ? +id : 0 });
    dispatch(uiModel.Tabs.actions.setActive(RvdTabs.my));

    navigate('/rvd');
  };

  const saveRvd = async (withoutSuccessNotify?: boolean) => {
    setIsUpdateLoading(true);
    try {
      checkEmployeesRvdType(
        [...usersToMutate.current.create, ...usersToMutate.current.update].map(user => ({
          employeeId: user.userInfo.id,
          endBreakTime: user.endBreakTime,
          endTime: user.endTime,
          isInVacation: user.isInVacation,
          startBreakTime: user.startBreakTime,
          startTime: user.startTime,
          toDate: user.toDate,
          type: checkRvdCompensationTypeToBack(user.type),
        })),
      );

      await updateRvdQuery({
        id: id ? +id : 0,
        status: currentRvd?.status || '',
        docSignatory: signatory?.personnelNumber || '',
        userComment: form.getFieldValue(FORM_USER_COMMENT),
      });
      for (const deleted of usersToMutate.current.delete) {
        await deleteUserQuery({ id: deleted });
      }
      for (const user of usersToMutate.current.update) {
        await updateUserQuery({
          employeeId: user.userInfo.id,
          wowId: currentRvd!.id,
          endBreakTime: user.endBreakTime,
          endTime: user.endTime,
          id: user.id!,
          isInVacation: user.isInVacation,
          startBreakTime: user.startBreakTime,
          startTime: user.startTime,
          toDate: user.toDate,
          type: checkRvdCompensationTypeToBack(user.type),
        });
      }

      const rvdUsersMutateCreate = usersToMutate.current.create.map(user => ({
        employeeId: user.userInfo.id,
        endBreakTime: user.endBreakTime,
        endTime: user.endTime,
        isInVacation: user.isInVacation,
        startBreakTime: user.startBreakTime,
        startTime: user.startTime,
        toDate: user.toDate,
        type: checkRvdCompensationTypeToBack(user.type),
      }));
      await createUserQuery({ wowId: currentRvd!.id, employee: rvdUsersMutateCreate });
      if (!withoutSuccessNotify) {
        notification.success({ message: 'Заявка на РВД изменена' });
      }
    } catch (e: any) {
      notification.error({ message: returnErrorOrMessage(e, 'Что-то пошло не так') });
    }
    setIsUpdateLoading(false);

    dispatch(uiModel.Tabs.actions.setActive(RvdTabs.my));
  };

  const onRefreshError = async () => {
    if (!currentRvd?.employees) {
      return;
    }

    try {
      await saveRvd(true);
      const users = currentRvd?.employees.map(elem => Number(elem.id));
      await checkErrors({ wowUsersIds: users });
      notification.success({ message: 'Обновлено!' });
      await getRvdById({ id: id ? +id : 0 });
      await getRvds({
        size: currentSize,
        page: currentPage,
        wowId: id ? +id : 0,
      });
    } catch (e: any) {
      notification.error({ message: returnErrorOrMessage(e, 'Что-то пошло не так') });
    }
  };

  const openDrawerCreate = () => {
    setIsSidebarCreateOpen(true);
  };

  return (
    <>
      <div className={style['top-side']}>
        <h3 className={style.title}>
          <Breadcrumbs breadCrumbs={breadCrumbs} />
        </h3>
        <div className={style.buttons}>
          {currentRvd?.status !== RvdStatuses.LOADED_CPRODUCT ? (
            <>
              {!currentRvd?.isComplete && currentRvd?.hr?.id === profile?.id && (
                <Button onClick={deleteRvd} loading={isDeleteLoading}>
                  <DeleteOutlined /> Удалить
                </Button>
              )}
              <Button type="primary" onClick={download}>
                <DownloadOutlined /> Скачать
              </Button>
              {!currentRvd?.isComplete && currentRvd?.hr?.id === profile?.id && (
                <Button onClick={openDrawerCreate}>
                  <EditOutlined />
                  Добавить сотрудника
                </Button>
              )}
              {!currentRvd?.isComplete && currentRvd?.hr?.id === profile?.id && (
                <Button
                  type="primary"
                  onClick={() => {
                    saveRvd();
                  }}
                  loading={isUpdateLoading}
                  disabled={!currentRvdUsers?.data.length || !currentRvd?.employees.length}
                >
                  <SaveOutlined /> Сохранить
                </Button>
              )}
            </>
          ) : null}

          <Button onClick={cancelModalOpen}>
            <StopOutlined /> Отмена
          </Button>
          <ModalCancelSubmit open={openCancelModal} onQuit={onSubmitCancelModal} onStay={onCancelCancelModal} />
        </div>
      </div>
      <RvdSidebar
        onClose={onClose}
        isOpen={isSidebarOpen}
        onOk={onFinish}
        currentRvd={currentRvd!}
        currentUser={currentUser}
      />
      <RvdSidebar onClose={onCreateClose} isOpen={isSidebarCreateOpen} onOk={onCreateFinish} currentRvd={currentRvd} />
      <div className={style.search}>
        <Search
          onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          placeholder="Введите табельный номер сотрудника"
          handleClear={onClear}
        />
      </div>
      <div className={style.statusData}>
        {rvdResponse?.docNumber && (
          <p>
            <span>№ документа в 1С:</span>
            <span>{rvdResponse?.docNumber}</span>
          </p>
        )}
        <p>
          <span>Статус документа:</span>
          <span>
            <BadgeData rvd={rvdResponse as TRvdResponseData} />
          </span>
        </p>
      </div>
      <Spin spinning={isUserFetching || isUserLoading || isRvdLoading}>
        <CommonTable
          dataSource={dataForRvdUsersTable(currentRvdUsers?.data ? currentRvdUsers?.data : [])}
          columns={columns}
          currentPage={currentPage}
          currentSize={currentSize}
          setCurrentPage={setCurrentPage}
          setCurrentSize={setCurrentSize}
          total={currentRvdUsers?.pagination.total}
          onclickToRow={rowClicked}
        />
      </Spin>

      <Form
        form={form}
        name="basic"
        layout="vertical"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        initialValues={{ remember: true }}
        onFinish={() => {}}
        autoComplete="off"
      >
        <EmployeeInput
          data={signatories}
          getEmployees={getSignatories}
          setEmployee={setSignatory}
          divisionName={signatory?.division}
          label="Выберите руководителя"
          rules={[{ required: true, message: 'Выберите руководителя!' }]}
          className="form-item-rvd"
          onChangeEmployee={() => {}}
          defaultUser={currentUser?.userInfo}
          formItemName={FORM_ITEM_NAME}
          disabled={
            rvdResponse?.status === RvdStatusesBack.APPROVED || rvdResponse?.status === RvdStatusesBack.CANCELED
          }
        />
        <Form.Item name={FORM_USER_COMMENT} label={'Введите комментарий'}>
          <Input
            placeholder="Введите комментарий"
            disabled={
              rvdResponse?.status === RvdStatusesBack.APPROVED || rvdResponse?.status === RvdStatusesBack.CANCELED
            }
          />
        </Form.Item>
      </Form>

      {!(rvdResponse?.status === RvdStatusesBack.APPROVED || rvdResponse?.status === RvdStatusesBack.CANCELED) &&
        (rvdResponse?.comment || !isLoadAllowed) &&
        currentRvd?.hr?.id === profile?.id && (
          <div className={style.error}>
            <p className={style.nullMargin}>Ошибка: </p>
            <Button onClick={onRefreshError} loading={isCheckLoading}>
              Сохранить и проверить заявку
            </Button>
          </div>
        )}
      {rvdResponse?.comment ? <p>{rvdResponse?.comment}</p> : null}
      {!isLoadAllowed &&
        currentRvd?.employees.map(user => {
          if (user.comment) {
            return <p>{user.comment}</p>;
          }
          return null;
        })}
    </>
  );
};

export default UpdateRvd;
